import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, FileText } from 'lucide-react';
import { ALL_SERVICES } from '@/pages/lp/lpConfig';

const API = process.env.REACT_APP_BACKEND_URL;
const WRITEUP_MAX = 1500;

/**
 * LpForm — Single-step PPC landing page lead form (mobile-first, inline validation).
 *
 * Field policy (matches backend PPC_ADS_ALLOWED_SLUGS / PPC_ADS_ALLOWED_SERVICES):
 *  - client_type: individual | business
 *  - first_name, last_name: 2-50 chars, required
 *  - business_name: 2-120 chars, required when client_type=business
 *  - email: EmailStr (Pydantic) + HTML5 type=email
 *  - phone: 10-15 digits, normalized to E.164 server-side
 *  - services_interested: 1-6 of allowed enum
 *  - brief_context: 0-1500 chars, optional but emphasized as "writeup" section
 *  - consent: must be true
 *  - honeypot company_url: must be empty (bots fill it)
 *
 * Mobile UX:
 *  - inputMode="email"/"tel" for keyboard switching
 *  - autoComplete tokens for browser autofill
 *  - Inline error display with aria-invalid (no need to scroll to a toast)
 *  - 44px tap targets, single column on small screens
 */
export const LpForm = ({ config }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [clientType, setClientType] = useState(
    config.audience === "individual" ? "individual" : config.audience === "business" ? "business" : "individual"
  );
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [services, setServices] = useState(config.services_pre_checked || []);
  const [briefContext, setBriefContext] = useState("");
  const [consent, setConsent] = useState(false);
  const [companyUrl, setCompanyUrl] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const setFieldError = (field, msg) => setErrors((prev) => ({ ...prev, [field]: msg }));
  const clearFieldError = (field) => setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });

  const toggleService = (id) => {
    setServices((prev) => {
      const next = prev.includes(id) ? (prev || []).filter((s) => s !== id) : [...prev, id];
      if (next.length > 0) clearFieldError("services");
      return next;
    });
  };

  const validateLocal = () => {
    const next = {};
    if (firstName.trim().length < 2) next.firstName = "Please enter your first name (2+ characters).";
    if (lastName.trim().length < 2) next.lastName = "Please enter your last name (2+ characters).";
    if (clientType === "business" && businessName.trim().length < 2) next.businessName = "Please enter your business name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = "Please enter a valid email address.";
    const phoneDigits = phone.replace(/\D/g, "");
    if (phoneDigits.length < 10 || phoneDigits.length > 15) next.phone = "Please enter a valid phone number (10–15 digits).";
    if (services.length === 0) next.services = "Please select at least one service.";
    if (!consent) next.consent = "Please agree to be contacted by KGOB.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validateLocal()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    setSubmitting(true);

    const lp_meta = {
      landing_page_url: window.location.href,
      referrer: document.referrer || null,
      utm_source: searchParams.get("utm_source"),
      utm_medium: searchParams.get("utm_medium"),
      utm_campaign: searchParams.get("utm_campaign"),
      utm_term: searchParams.get("utm_term"),
      utm_content: searchParams.get("utm_content"),
      gclid: searchParams.get("gclid"),
    };

    try {
      const { data } = await axios.post(`${API}/api/v1/leads/ppc-ads`, {
        lp_slug: config.slug,
        client_type: clientType,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        business_name: clientType === "business" ? businessName.trim() : null,
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        services_interested: services,
        brief_context: briefContext.trim() || null,
        consent,
        honeypot_field: companyUrl,
        lp_meta,
      });

      if (typeof window !== "undefined") {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "lp_lead_submit",
          lp_slug: config.slug,
          client_type: clientType,
          services_count: services.length,
          has_writeup: briefContext.trim().length > 0,
        });
      }

      navigate(`${data.redirect_url || `/lp/${config.slug}/thank-you`}?ref=${data.lead_id || ""}`, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        toast.error("Too many submissions. Please wait a minute and try again.");
      } else if (status === 400 && detail) {
        toast.error(typeof detail === "string" ? detail : "Please check your entries.");
      } else {
        toast.error("Submission failed. Please try again or call us directly.");
      }
      setSubmitting(false);
    }
  };

  // Shared input class (mobile-first: full-width, 44px+ tap target)
  const inputBase =
    "w-full rounded-lg border bg-white px-3 py-3 text-base text-[#0B2240] focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 transition";
  const inputOk = "border-[#E8E5DC] focus:border-[#C9A961]";
  const inputErr = "border-red-400 focus:border-red-500 focus:ring-red-200";

  return (
    <section
      id="lp-form"
      className="bg-[#F7F6F2] py-12 lg:py-16"
      data-testid="lp-form-section"
    >
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl bg-white shadow-xl border border-[#E8E5DC] overflow-hidden">
          <div className="bg-[#0B2240] text-white px-5 py-5 sm:px-8">
            <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-semibold">
              {config.cta}
            </h2>
            <p className="text-sm text-white/70 mt-1">
              Every request is reviewed personally by a KGOB CPA. We&apos;ll follow up within 1 business day.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="px-5 py-6 sm:px-8 sm:py-8 space-y-5" data-testid="lp-form" noValidate>
            {/* Honeypot — hidden from real users */}
            <input data-testid="lp-formx-input-2"
              type="text"
              name="company_url"
              tabIndex={-1}
              autoComplete="off"
              value={companyUrl}
              onChange={(e) => setCompanyUrl(e.target.value)}
              style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
              aria-hidden="true"
            />

            {/* Client type toggle */}
            {config.audience === "both" && (
              <div>
                <label className="block text-sm font-medium text-[#0B2240] mb-2">
                  I am a&hellip;
                </label>
                <div className="grid grid-cols-2 gap-2" data-testid="lp-client-type-group">
                  {[
                    { id: "individual", label: "Individual" },
                    { id: "business", label: "Business owner" },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setClientType(opt.id)}
                      className={`min-h-[44px] rounded-lg border px-4 py-2.5 text-sm font-medium transition ${
                        clientType === opt.id
                          ? "border-[#C9A961] bg-[#C9A961]/10 text-[#0B2240]"
                          : "border-[#E8E5DC] bg-white text-[#4C5A70] hover:border-[#C9A961]/50"
                      }`}
                      data-testid={`lp-client-type-${opt.id}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Names */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="lp-fn" className="block text-sm font-medium text-[#0B2240] mb-1.5">
                  First name <span className="text-red-500">*</span>
                </label>
                <input
                  id="lp-fn"
                  type="text"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => { setFirstName(e.target.value); if (errors.firstName) clearFieldError("firstName"); }}
                  minLength={2}
                  maxLength={50}
                  required
                  aria-invalid={!!errors.firstName}
                  aria-describedby={errors.firstName ? "lp-fn-err" : undefined}
                  className={`${inputBase} ${errors.firstName ? inputErr : inputOk}`}
                  data-testid="lp-first-name"
                />
                {errors.firstName && (
                  <p id="lp-fn-err" className="mt-1 text-xs text-red-600" data-testid="lp-first-name-error">{errors.firstName}</p>
                )}
              </div>
              <div>
                <label htmlFor="lp-ln" className="block text-sm font-medium text-[#0B2240] mb-1.5">
                  Last name <span className="text-red-500">*</span>
                </label>
                <input
                  id="lp-ln"
                  type="text"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => { setLastName(e.target.value); if (errors.lastName) clearFieldError("lastName"); }}
                  minLength={2}
                  maxLength={50}
                  required
                  aria-invalid={!!errors.lastName}
                  aria-describedby={errors.lastName ? "lp-ln-err" : undefined}
                  className={`${inputBase} ${errors.lastName ? inputErr : inputOk}`}
                  data-testid="lp-last-name"
                />
                {errors.lastName && (
                  <p id="lp-ln-err" className="mt-1 text-xs text-red-600" data-testid="lp-last-name-error">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Business name (conditional) */}
            {clientType === "business" && (
              <div data-testid="lp-business-name-wrapper">
                <label htmlFor="lp-bn" className="block text-sm font-medium text-[#0B2240] mb-1.5">
                  Business name <span className="text-red-500">*</span>
                </label>
                <input
                  id="lp-bn"
                  type="text"
                  autoComplete="organization"
                  value={businessName}
                  onChange={(e) => { setBusinessName(e.target.value); if (errors.businessName) clearFieldError("businessName"); }}
                  minLength={2}
                  maxLength={120}
                  required
                  aria-invalid={!!errors.businessName}
                  aria-describedby={errors.businessName ? "lp-bn-err" : undefined}
                  className={`${inputBase} ${errors.businessName ? inputErr : inputOk}`}
                  data-testid="lp-business-name"
                />
                {errors.businessName && (
                  <p id="lp-bn-err" className="mt-1 text-xs text-red-600" data-testid="lp-business-name-error">{errors.businessName}</p>
                )}
              </div>
            )}

            {/* Email + phone */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="lp-em" className="block text-sm font-medium text-[#0B2240] mb-1.5">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  id="lp-em"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (errors.email) clearFieldError("email"); }}
                  required
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? "lp-em-err" : undefined}
                  className={`${inputBase} ${errors.email ? inputErr : inputOk}`}
                  data-testid="lp-email"
                />
                {errors.email && (
                  <p id="lp-em-err" className="mt-1 text-xs text-red-600" data-testid="lp-email-error">{errors.email}</p>
                )}
              </div>
              <div>
                <label htmlFor="lp-ph" className="block text-sm font-medium text-[#0B2240] mb-1.5">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  id="lp-ph"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={phone}
                  onChange={(e) => { setPhone(e.target.value); if (errors.phone) clearFieldError("phone"); }}
                  required
                  maxLength={20}
                  placeholder="(704) 555-1234"
                  aria-invalid={!!errors.phone}
                  aria-describedby={errors.phone ? "lp-ph-err" : undefined}
                  className={`${inputBase} ${errors.phone ? inputErr : inputOk}`}
                  data-testid="lp-phone"
                />
                {errors.phone && (
                  <p id="lp-ph-err" className="mt-1 text-xs text-red-600" data-testid="lp-phone-error">{errors.phone}</p>
                )}
              </div>
            </div>

            {/* Services */}
            <div>
              <label className="block text-sm font-medium text-[#0B2240] mb-2">
                What can we help with? <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="lp-services-group">
                {(ALL_SERVICES || []).map((svc) => {
                  const checked = services.includes(svc.id);
                  return (
                    <label
                      key={svc.id}
                      className={`flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-3 text-sm transition min-h-[44px] ${
                        checked
                          ? "border-[#C9A961] bg-[#C9A961]/10 text-[#0B2240]"
                          : `${errors.services ? "border-red-300" : "border-[#E8E5DC]"} bg-white text-[#4C5A70] hover:border-[#C9A961]/50`
                      }`}
                      data-testid={`lp-service-${svc.id}`}
                    >
                      <input data-testid="lp-formx-input-1"
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleService(svc.id)}
                        className="h-4 w-4 rounded border-[#C9A961]/50 text-[#C9A961] focus:ring-[#C9A961]/30"
                      />
                      <span className="font-medium">{svc.label}</span>
                    </label>
                  );
                })}
              </div>
              {errors.services && (
                <p className="mt-1.5 text-xs text-red-600" data-testid="lp-services-error">{errors.services}</p>
              )}
            </div>

            {/* CLIENT WRITEUP — prominent, emphasized */}
            <div className="rounded-xl bg-[#F7F6F2] border border-[#E8E5DC] p-4 sm:p-5" data-testid="lp-writeup-block">
              <div className="flex items-center gap-2 mb-1.5">
                <FileText className="h-4 w-4 text-[#C9A961]" />
                <label htmlFor="lp-wu" className="text-sm font-semibold text-[#0B2240]">
                  Tell us about your situation
                </label>
                <span className="text-xs text-[#4C5A70] font-normal">(optional, but recommended)</span>
              </div>
              <p className="text-xs text-[#4C5A70] mb-2.5 leading-relaxed">
                Share your goals, deadlines, current pain points, the software you use — anything that helps us prepare for your first call. The more context, the better we can help.
              </p>
              <textarea
                id="lp-wu"
                value={briefContext}
                onChange={(e) => setBriefContext(e.target.value.slice(0, WRITEUP_MAX))}
                rows={5}
                maxLength={WRITEUP_MAX}
                autoComplete="off"
                placeholder="e.g. We're a 12-person LLC in Charlotte. Switched from QuickBooks to Xero last year. Need help with quarterly estimates and a multi-state nexus question for SC. Looking to talk before March 15."
                className="w-full rounded-lg border border-[#E8E5DC] bg-white px-3 py-3 text-base text-[#0B2240] focus:border-[#C9A961] focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 resize-y leading-relaxed"
                data-testid="lp-brief-context"
              />
              <div className="mt-1.5 flex justify-end">
                <span className={`text-xs ${briefContext.length >= WRITEUP_MAX - 50 ? "text-amber-600" : "text-[#4C5A70]"}`} data-testid="lp-writeup-counter">
                  {briefContext.length} / {WRITEUP_MAX}
                </span>
              </div>
            </div>

            {/* Consent */}
            <div>
              <label className="flex items-start gap-2.5 cursor-pointer min-h-[44px]">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => { setConsent(e.target.checked); if (errors.consent) clearFieldError("consent"); }}
                  className="mt-1 h-4 w-4 rounded border-[#C9A961]/50 text-[#C9A961] focus:ring-[#C9A961]/30"
                  aria-invalid={!!errors.consent}
                  data-testid="lp-consent"
                />
                <span className="text-xs text-[#4C5A70] leading-relaxed">
                  I agree KGOB may contact me by phone, email, or SMS regarding this inquiry. KGOB respects your privacy — see our{" "}
                  <a data-testid="lp-formx-a-1" href="/privacy-policy" className="text-[#0B2240] underline hover:text-[#C9A961]">
                    Privacy Policy
                  </a>
                  .
                </span>
              </label>
              {errors.consent && (
                <p className="mt-1 text-xs text-red-600" data-testid="lp-consent-error">{errors.consent}</p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#0B2240] hover:bg-[#0d2a55] text-white font-semibold px-6 py-4 text-base transition disabled:opacity-60 disabled:cursor-not-allowed min-h-[48px]"
              data-testid="lp-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Submitting&hellip;
                </>
              ) : (
                config.cta
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};
